import {
  ShowBase,
  TextField,
  ReferenceField,
  useRecordContext,
  useGetList,
  useGetManyReference,
} from "react-admin";
import { useCanAccess } from "@react-admin/ra-rbac";
import { Link } from "react-router-dom";
import { Typography, Card, Box, Grid, useMediaQuery, Theme } from "@mui/material";
import Label from "../../components/Label";
import ComponentShow, { ShowActions } from "../../components/PageComponents/ComponentShow";
import DetailHeader from "../../components/PageComponents/DetailHeader";
import AnimatedBlock from "../../components/Common/AnimatedBlock";
import InfoRow from "../../components/Common/InfoRow";
import QuickViewCard from "../../components/Common/QuickViewCard";
import { formatDate, formatDateTime } from "../../utils/paymentFormat";
import { useTranslation } from "react-i18next";

export const IsResellerLabel = ({
  statusField = "isReseller",
  record: propRecord = null,
}: {
  statusField?: string;
  record?: any;
}) => {
  const { t }: { t: any } = useTranslation();
  const recordContext = useRecordContext();
  const record = propRecord || recordContext;
  const isReseller = Boolean(record?.[statusField]);

  return <Label color={isReseller ? "primary" : "secondary"}>{isReseller ? t("Reseller") : t("Client")}</Label>;
};

const EntityCount = () => {
  const record = useRecordContext();
  const { total, isLoading, error } = useGetManyReference("projects", {
    target: "accountId",
    id: record?.id,
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "id", order: "DESC" },
  });
  if (isLoading || error) return <Typography component="span" sx={{ fontWeight: 700, fontSize: 14 }} />;
  return <>{total}</>;
};

const ParentAccount = () => {
  const { t }: { t: any } = useTranslation();
  return (
    <ReferenceField source="parentAccountId" reference="accounts" link="show" emptyText={t("None")}>
      <TextField source="name" sx={{ fontWeight: 700 }} />
    </ReferenceField>
  );
};

const ResellerClients = () => {
  const { t }: { t: any } = useTranslation();
  const record = useRecordContext();
  const { canAccess } = useCanAccess({ action: "read", resource: "accounts.tab.clients" });
  const enabled = Boolean(record?.isReseller && canAccess);
  const { data = [], isLoading } = useGetList("accountsResellerClients", { filter: { id: record?.id } }, { enabled });
  if (!enabled) return null;
  return (
    <Box mt={4}>
      <Box pb={2}>
        <Typography variant="h6">{t("Clients")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Client accounts attached to this reseller.")}
        </Typography>
      </Box>
      <Card>
        <Box px={2} py={1}>
          {isLoading ? null : data.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1.25 }}>
              {t("No client account")}
            </Typography>
          ) : (
            data.map((client: any) => (
              <InfoRow key={client.id} label={<Link to={`/accounts/${client.id}/show`}>{client.name || client.id}</Link>}>
                {client.dateCreated && formatDate(client.dateCreated)}
              </InfoRow>
            ))
          )}
        </Box>
      </Card>
    </Box>
  );
};

const Details = () => {
  const { t }: { t: any } = useTranslation();
  const record = useRecordContext();
  return (
    <Box>
      <Box pb={2}>
        <Typography variant="h6">{t("Account details")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Identity of the account and its place in the reseller hierarchy.")}
        </Typography>
      </Box>
      <AnimatedBlock>
        <Card>
          <Box px={2} py={1}>
            <InfoRow label={t("Name")}>{record.name}</InfoRow>
            <InfoRow label={t("Type")}>
              <IsResellerLabel />
            </InfoRow>
            <InfoRow label={t("Parent reseller account")}>
              <ParentAccount />
            </InfoRow>
            <InfoRow label={t("Number of entities")}>
              <EntityCount />
            </InfoRow>
            <InfoRow label={t("Created")}>{formatDateTime(record.dateCreated)}</InfoRow>
          </Box>
        </Card>
      </AnimatedBlock>
      <ResellerClients />
    </Box>
  );
};

const QuickView = () => {
  const { t }: { t: any } = useTranslation();
  return (
    <QuickViewCard>
      <InfoRow label={t("Type")}>
        <IsResellerLabel />
      </InfoRow>
      <InfoRow label={t("Number of entities")}>
        <EntityCount />
      </InfoRow>
      <InfoRow label={t("Parent reseller account")}>
        <ParentAccount />
      </InfoRow>
    </QuickViewCard>
  );
};

const ContentShow = () => {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));

  if (isMobile) {
    return (
      <Box sx={{ px: 2, mt: 2, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <ShowActions resource="accounts" />
        </Box>
        <Details />
        <Box sx={{ mt: 4 }}>
          <QuickView />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      <Grid container direction="row" justifyContent="center" spacing={4} sx={{ px: 4 }}>
        <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ShowActions resource="accounts" />
        </Grid>
        <Grid item xs={12} md={8}>
          <Details />
        </Grid>
        <Grid item xs={12} md={4}>
          <QuickView />
        </Grid>
      </Grid>
    </Box>
  );
};

const Content = () => {
  const { t }: { t: any } = useTranslation();
  const record = useRecordContext();
  if (!record) return null;

  return (
    <ComponentShow
      header={
        <DetailHeader
          kind="Account"
          title={record.name}
          status={<IsResellerLabel />}
          facts={[record.dateCreated && `${t("Created")} ${formatDate(record.dateCreated)}`]}
        />
      }
    >
      <ContentShow />
    </ComponentShow>
  );
};

export const AccountShow = () => (
  <ShowBase>
    <Content />
  </ShowBase>
);
